import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaSearch, FaFilter, FaHeart, FaComment, FaMapMarkerAlt, 
  FaFire, FaStar, FaChevronLeft, FaChevronRight, FaCheckCircle, FaCrown, FaGem, 
  FaShieldAlt, FaBolt, FaRibbon, FaTimes 
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Loader from '../common/Loader';
import toast from 'react-hot-toast';
import './discover.css';

const Discover = () => {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [paginatedProfiles, setPaginatedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [likedProfiles, setLikedProfiles] = useState(new Set());
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // 6 profils par page
  const [totalPages, setTotalPages] = useState(1);
  
  const [filters, setFilters] = useState({
    gender: 'all',
    orientation: 'all',
    ageMin: 18,
    ageMax: 60,
    online: false,
    verified: false,
    ville: ''
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const UPLOADS_URL = 'https://murima.julesferry-ga.com/uploads/';

  useEffect(() => {
    // Récupérer le paramètre de recherche de l'URL
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
    
    loadProfiles();
  }, [location]);

  useEffect(() => {
    applyFilters();
  }, [profiles, filters, searchTerm]);

  useEffect(() => {
    // Mettre à jour la pagination quand les filtres changent
    setTotalPages(Math.ceil(filteredProfiles.length / itemsPerPage));
    setCurrentPage(1); // Retour à la première page quand les filtres changent
  }, [filteredProfiles, itemsPerPage]);

  useEffect(() => {
    // Appliquer la pagination
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setPaginatedProfiles(filteredProfiles.slice(start, end));
  }, [filteredProfiles, currentPage, itemsPerPage]);
  
// Dans loadProfiles, assurez-vous de bien filtrer l'utilisateur connecté
// Ajoutez cette fonction calculateAge dans votre composant (avant loadProfiles)
const calculateAge = (dateNaissance) => {
  if (!dateNaissance) return null;
  try {
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch (e) {
    return null;
  }
};



const loadProfiles = async () => {
  setLoading(true);
  try {
    console.log('📡 Chargement des profils pour utilisateur:', user?.id);
    
    const response = await api.get('/get-hot-matches.php', {
      params: {
        user_id: user?.id
      }
    });
    
    console.log('📥 Profils chargés:', response.data);
    
    if (response.data.success) {
      const otherProfiles = (response.data.matches || []).filter(
        profile => profile.id !== user?.id
      );
      
      console.log('👥 Autres profils (excluant user):', otherProfiles.length);
      
      const formattedProfiles = otherProfiles.map(profile => {
        let age = profile.age;
        if (!age && profile.date_naissance) {
          age = calculateAge(profile.date_naissance);
        }
        
        return {
          id: profile.id,
          pseudo: profile.pseudo,
          age: age || Math.floor(Math.random() * 20) + 20,
          ville: profile.ville || 'Paris',
          pays: profile.pays || 'France',
          avatar: profile.avatar || profile.avatar,
          en_ligne: Boolean(profile.en_ligne),
          is_verified: Boolean(profile.is_verified),
          is_premium: Boolean(profile.is_premium),
          bio: profile.bio || 'Aucune bio pour le moment',
          genre: profile.genre || 'autre',
          orientation: profile.orientation || 'autre',
          interests: profile.interests || [],
          distance: profile.distance || Math.floor(Math.random() * 50) + 1,
          derniere_connexion: profile.derniere_connexion,
          already_liked: Boolean(profile.already_liked) // 🔥 Important
        };
      });
      
      console.log('✅ Profils formatés:', formattedProfiles);
      setProfiles(formattedProfiles);
      
      // Initialiser les profils likés
      const liked = new Set();
      formattedProfiles.forEach(p => {
        if (p.already_liked) liked.add(p.id);
      });
      setLikedProfiles(liked);
    } else {
      toast.error('Erreur lors du chargement des profils');
    }
  } catch (error) {
    console.error('❌ Erreur chargement profils:', error);
    toast.error('Impossible de charger les profils');
  } finally {
    setLoading(false);
  }
};



  const applyFilters = () => {
    console.log('🔍 Application des filtres...');
    let filtered = [...profiles];

    // Filtre par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.pseudo.toLowerCase().includes(term) ||
        p.ville.toLowerCase().includes(term) ||
        p.bio?.toLowerCase().includes(term)
      );
    }

    // Filtre par genre
    if (filters.gender !== 'all') {
      filtered = filtered.filter(p => p.genre === filters.gender);
    }

    // Filtre par orientation
    if (filters.orientation !== 'all') {
      filtered = filtered.filter(p => p.orientation === filters.orientation);
    }

    // Filtre par âge
    filtered = filtered.filter(p => 
      p.age >= filters.ageMin && p.age <= filters.ageMax
    );

    // Filtre en ligne
    if (filters.online) {
      filtered = filtered.filter(p => p.en_ligne);
    }

    // Filtre vérifié
    if (filters.verified) {
      filtered = filtered.filter(p => p.is_verified);
    }

    // Filtre par ville
    if (filters.ville) {
      filtered = filtered.filter(p => 
        p.ville.toLowerCase().includes(filters.ville.toLowerCase())
      );
    }

    console.log('📊 Résultats filtrés:', filtered.length);
    setFilteredProfiles(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

const handleLike = async (profileId, e) => {
  e.stopPropagation();
  
  // Vérifier que ce n'est pas son propre profil
  if (user?.id === profileId) {
    toast.error('Vous ne pouvez pas liker votre propre profil');
    return;
  }
  
  if (likedProfiles.has(profileId)) {
    toast('Vous avez déjà liké ce profil', { icon: '💔' });
    return;
  }
  
  try {
    // 🔥 CORRECTION : Envoyer les deux IDs correctement
    const response = await api.post('/like.php', {
      user_id: user?.id,      // L'utilisateur connecté (from_user_id)
      to_user_id: profileId    // Le profil à liker (to_user_id)
    });
    
    console.log('📥 Like response:', response.data);
    
    if (response.data.success) {
      if (response.data.action === 'liked') {
        setLikedProfiles(prev => new Set(prev).add(profileId));
        
        setProfiles(prev => 
          prev.map(p => 
            p.id === profileId ? { ...p, already_liked: true } : p
          )
        );
        
        if (response.data.match) {
          toast.success('C\'est un match ! 🔥', { duration: 5000 });
          // Optionnel: afficher une popup de match
        } else {
          toast.success('Like envoyé !');
        }
      } else if (response.data.action === 'unliked') {
        setLikedProfiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(profileId);
          return newSet;
        });
        
        setProfiles(prev => 
          prev.map(p => 
            p.id === profileId ? { ...p, already_liked: false } : p
          )
        );
        
        toast.success('Like retiré');
      }
    } else {
      if (response.data.error === 'already_liked') {
        toast('Vous avez déjà liké ce profil', { icon: '💔' });
      } else {
        toast.error(response.data.error || 'Erreur lors du like');
      }
    }
  } catch (error) {
    console.error('❌ Erreur like:', error);
    toast.error('Erreur réseau');
  }
};

  // Pagination functions
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getImageUrl = (path) => {
    if (!path) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (path.startsWith('http')) return path;
    return `${UPLOADS_URL}${path}`;
  };

  const formatDistance = (km) => {
    if (!km) return 'Distance inconnue';
    if (km < 1) return 'À moins de 1 km';
    if (km === 1) return 'À 1 km';
    return `À ${km} km`;
  };

// Dans votre composant Discover ou Dashboard
const formatLastSeen = (date) => {
  if (!date) return { text: 'Hors ligne', isOnline: false };
  if (date === '0000-00-00 00:00:00') return { text: 'Hors ligne', isOnline: false };
  
  try {
    const last = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - last) / (1000 * 60)); // différence en minutes
    
    // En ligne (moins de 5 minutes)
    if (diff < 5) {
      return { text: 'En ligne', isOnline: true };
    }
    
    // Récemment en ligne
    if (diff < 60) {
      return { text: `Il y a ${diff} min`, isOnline: false };
    }
    if (diff < 120) {
      return { text: 'Il y a 1h', isOnline: false };
    }
    if (diff < 1440) {
      return { text: `Il y a ${Math.floor(diff / 60)}h`, isOnline: false };
    }
    if (diff < 2880) {
      return { text: 'Hier', isOnline: false };
    }
    if (diff < 10080) {
      return { text: `Il y a ${Math.floor(diff / 1440)}j`, isOnline: false };
    }
    
    return { text: 'Hors ligne', isOnline: false };
  } catch (e) {
    return { text: 'Hors ligne', isOnline: false };
  }
};

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="discover-page">
      {/* Barre de recherche */}
      <div className="discover-search">
  <div className="search-container">
    <div className="search-box">
      <FaSearch className="search-icon" />
      <input
        type="text"
        placeholder="Rechercher un pseudo, une ville..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-input"
      />
      {searchTerm && (
        <button 
          className="search-clear"
          onClick={() => setSearchTerm('')}
          aria-label="Effacer la recherche"
        >
          <FaTimes />
        </button>
      )}
    </div>
    
    <button 
      className={`filter-button ${showFilters ? 'active' : ''}`}
      onClick={() => setShowFilters(!showFilters)}
      aria-label="Filtrer les résultats"
    >
      <div className="filter-icon-wrapper">
        <FaFilter className="filter-icon" />
        {showFilters && <span className="filter-dot"></span>}
      </div>
      <span className="filter-text">Filtres</span>
      {/* <div className="filter-badge">
        <span></span>
      </div> */}
    </button>
  </div>
</div>

      {/* Filtres */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Genre</label>
              <select name="gender" value={filters.gender} onChange={handleFilterChange}>
                <option value="all">Tous</option>
                <option value="homme">Hommes</option>
                <option value="femme">Femmes</option>
                <option value="non-binaire">Non-binaires</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Orientation</label>
              <select name="orientation" value={filters.orientation} onChange={handleFilterChange}>
                <option value="all">Toutes</option>
                <option value="hetero">Hétéro</option>
                <option value="homo">Homo</option>
                <option value="bi">Bi</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Âge minimum</label>
              <input
                type="range"
                name="ageMin"
                min="18"
                max="80"
                value={filters.ageMin}
                onChange={handleFilterChange}
              />
              <span className="range-value">{filters.ageMin} ans</span>
            </div>

            <div className="filter-group">
              <label>Âge maximum</label>
              <input
                type="range"
                name="ageMax"
                min="18"
                max="80"
                value={filters.ageMax}
                onChange={handleFilterChange}
              />
              <span className="range-value">{filters.ageMax} ans</span>
            </div>

            <div className="filter-group">
              <label>Ville</label>
              <input
                type="text"
                name="ville"
                placeholder="Ex: Paris"
                value={filters.ville}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group checkboxes">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="online"
                  checked={filters.online}
                  onChange={handleFilterChange}
                />
                <span>En ligne uniquement</span>
              </label>

              <label className="checkbox">
                <input
                  type="checkbox"
                  name="verified"
                  checked={filters.verified}
                  onChange={handleFilterChange}
                />
                <span>Profils vérifiés</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Résultats */}
      <div className="discover-results">
        <p className="results-count">
          {filteredProfiles.length} résultat{filteredProfiles.length > 1 ? 's' : ''} trouvé{filteredProfiles.length > 1 ? 's' : ''}
          {filteredProfiles.length > itemsPerPage && ` • Page ${currentPage}/${totalPages}`}
        </p>

        <div className="profiles-grid">
          {paginatedProfiles.length > 0 ? (
            paginatedProfiles.map(profile => (
              <div 
                key={profile.id} 
                className="profile-card"
                onClick={() => navigate(`/profile/${profile.id}`)}
              >
                <div className="profile-image">
                  <img src={getImageUrl(profile.avatar)} alt={profile.pseudo} />
                  {profile.en_ligne && <span className="online-indicator"></span>}
                  <div className="profile-badges">
                    {profile.is_verified && (
    <span className="badge-verified" title="Profil vérifié">
      <FaCheckCircle  />
    </span>
  )}
  {profile.is_premium && (
    <span className="badge-premium" title="Membre premium">
      <FaCrown />
    </span>
  )}
                  </div>
                  <div className="profile-distance">
                    <FaMapMarkerAlt /> {formatDistance(profile.distance)} - {profile.ville}, {profile.pays}
                  </div>
                </div>

                <div className="profile-info">
                  <div className="profile-header">
                    <h3>{profile.pseudo}, {profile.age}</h3>
                   
                  </div>

                  <p className="bio" style={{marginTop:'50px'}}>{profile.bio?.substring(0, 100)}...</p>

                  <div className="interests">
                    {profile.interests?.slice(0, 3).map((interest, i) => (
                      <span key={i} className="interest-tag">{interest}</span>
                    ))}
                    {profile.interests?.length > 3 && (
                      <span className="interest-tag more">+{profile.interests.length - 3}</span>
                    )}
                  </div>

                  {/* Section des profils dans Discover.jsx */}
<div className="profile-footer">
  {(() => {
    const status = formatLastSeen(profile.derniere_connexion);
    return (
      <span className={`last-seen ${status.isOnline ? 'online' : 'offline'}`}>
        {status.text}
      </span>
    );
  })()}
  <div className="profile-actions">
    <button 
      className={`btn-like ${likedProfiles.has(profile.id) ? 'liked' : ''}`}
      onClick={(e) => handleLike(profile.id, e)}
      disabled={likedProfiles.has(profile.id)}
    >
      <FaHeart />
    </button>
    <button 
      className="btn-like"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/messages?user=${profile.id}`);
      }}
    >
      <FaComment />
    </button>
  </div>
</div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              <FaFire className="no-results-icon" />
              <h3>Aucun profil trouvé</h3>
              <p>Essayez de modifier vos filtres ou votre recherche</p>
              <button 
                className="btn-reset-filters"
                onClick={() => {
                  setFilters({
                    gender: 'all',
                    orientation: 'all',
                    ageMin: 18,
                    ageMax: 60,
                    online: false,
                    verified: false,
                    ville: ''
                  });
                  setSearchTerm('');
                }}
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination">
              <button 
                className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                <FaChevronLeft /> Précédent
              </button>
              
              <div className="pagination-pages">
                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  
                  // Afficher les pages avec logique d'ellipse
                  if (
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 || 
                    page === currentPage + 2
                  ) {
                    return <span key={page} className="pagination-dots">...</span>;
                  }
                  return null;
                })}
              </div>

              <button 
                className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Suivant <FaChevronRight />
              </button>
            </div>
            
            <div className="pagination-info">
              Page {currentPage} sur {totalPages} • {filteredProfiles.length} profils au total
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Discover;